import React from 'react';
import styles from 'views/MockServices/Tabs/MockTemplates/MockTemplatesListBlazeTable.module.css';
import { ApplicationState } from 'store/rootReducer';
import { connect } from 'react-redux';
import { confirm } from 'components/ui/modal/BlazeConfirmationModal';
import _ from 'lodash';
import { withTranslation, WithTranslation } from 'react-i18next';
import BlazeTable from 'components/blazeTable/BlazeTable';
import { BlazeTableColDef, BlazeTableOptions } from 'components/blazeTable/BlazeTable.types';
import { IToast, ToastType } from 'store/ui/toasts/toastsTypes';
import { hideToast, showToastNotification } from 'store/ui/toasts/toastsActions';
import { applySearchTermFilterToListWithTags } from 'utils/filterUtils';
import { fetchServices } from 'store/services/servicesActions';
import ExpandLink from 'components/blazeTable/ExpandLink';
import BzmIcon from 'components/icons/BzmIcon';
import ElevioTooltip from 'components/overlays/helpTooltip/TitleHelpTooltip/ElevioTooltip';
import { getConfigurationsListAction } from 'store/configurations/configurationsActions';
import {
  bulkDeleteAction,
  bulkStartAction,
  bulkStopAction,
  createBlankMockServiceGroup,
  deleteMockServiceGroup,
  deployMockServiceGroup,
  getMockServiceGroupList,
  stopMockServiceGroup,
  updateMockServiceGroupPatch,
} from '../../../../store/mockServiceGroups/mockServiceGroupsActions';
import { MockServiceGroup } from '../../../../store/mockServiceGroups/mockServiceGroupTypes';
import { expandGroup } from '../../../../store/mockServiceGroups/mockServiceGroupReducer';
import Locations from '../MockServices/Locations';
import { getLocValue, isOplLocation } from '../../../../utils/mockServiceUtils';
import GroupDetail from '../Groups/GroupDetail';
import { getPrivateLocationConfigUrl, isPrivateDataEnabled } from '../../../../utils/bzmUserConfigUtil';
import HelpTooltip from '../../../../components/overlays/helpTooltip/HelpTooltip';
import { MetaData, MockService, MockServiceMetaData, TRANSACTIONAL } from '../../../../types/rest/mockServiceTypes';
import { getStatusCell } from '../MockServices/MockServicesListBlazeTable';
import Endpoint from '../../../../components/endPoint/Endpoint';
import clsx from 'clsx';
import { PROVIDER_OPL } from '../../../../constants/bzm-constants';
import { CallbackFn, useFormInput, ValidatorType } from '../../../../hooks/useFormInput';
import BzFormControlWithHook from '../../../../components/forms/BzFormControlWithHook';
import i18n from '../../../../i18n';
import { Button } from 'react-bootstrap';
import { getSelectedCountTitle } from '../../../../utils/miscUtil';

type AllProps = StateProps & DispatchProps & WithTranslation;

class MockGroupListBlazeTable extends React.Component<AllProps> {
  tableRef: any = React.createRef<BlazeTable>();
  locSettings = getPrivateLocationConfigUrl();
  useLastValidValue = false;

  state = {
    selectedSmGroups: [] as MockServiceGroup[],
  };

  componentDidUpdate(prevProps: Readonly<StateProps>): void {
    //when expanding Group force to use last valid name value
    if (!_.isEqual(prevProps.expandedGroupId, this.props.expandedGroupId)) {
      this.useLastValidValue = true;
    }
  }

  onSelectionChange = (mocks: MockServiceGroup[]) => {
    this.setState({ selectedSmGroups: mocks });
  };

  componentDidMount() {
    this.props.fetchServices(true);
    this.props.getConfigurationsListAction();
    this.props.getMockServiceGroups();
  }

  getTable = (): BlazeTable => {
    return this.tableRef as BlazeTable;
  };

  goToFirstPage = () => {
    if (this.tableRef) {
      this.tableRef.pageSelectedHandler(1);
    }
  };

  stopSmGroup = (smGroup: MockServiceGroup) => {
    this.props.stopMockServiceGroup(smGroup);
  };

  deployMockService = async (smGroup: MockServiceGroup) => {
    this.props.deployMockServiceGroup(smGroup);
  };

  deleteMockGroup = (rowData: MockServiceGroup) => {
    const { t } = this.props;
    const modalBody = (
      <p>
        {t('groups:mockServiceGroupTable.deleteConfirmation')} <b className="text-break">{rowData.name}</b>?
      </p>
    );
    confirm({
      confirmation: modalBody,
      title: t('groups:mockServiceGroupTable.delete'),
      yesOptionTitle: t('common:actions.delete'),
    })
      .then(() => {
        this.props.deleteMockServiceGroup(rowData);
      })
      .catch(error => {
        //nothing
        console.error(error);
      });
  };

  getRightSideButtonsCell = (rowData: MockServiceGroup) => {
    const { t } = this.props;
    const stopSmGroupBtn = (rowData: any) => (
      <button
        id={`smGroupStopButton` + rowData.id}
        className="btn btn-secondary btn-square"
        title={t('groups:mockServiceGroupTable.stopGroup')}
        onClick={() => this.stopSmGroup(rowData)}
      >
        <BzmIcon variant={'icon-bzm-stop-abort'} />
      </button>
    );

    const runSmGroupBtn = (rowData: MockServiceGroup) => (
      <button
        /* disabled={!_.isNil(rowData.status)}*/
        id={`mockServiceStatusRunSmGroupButton` + rowData.id}
        className="btn btn-success btn-square"
        title={t('groups:mockServiceGroupTable.runGroupTitle')}
        disabled={rowData.status === 'CONFIGURING'}
        onClick={() => this.deployMockService(rowData)}
      >
        <BzmIcon variant={'icon-bzm-play-run'} />
      </button>
    );

    const msGroupDeleteBtn = (rowData: MockServiceGroup) => (
      <button className="btn btn-link-dark btn-no-hover" onClick={() => this.deleteMockGroup(rowData)}>
        <BzmIcon variant={'icon-bzm-delete'} className={'icon-xs'} />
      </button>
    );

    return (
      <>
        {/* Play & Stop buttons */}
        <div className="d-inline-block mr-2">
          {!_.isNil(rowData.status) && rowData.status !== 'RUNNING' ? runSmGroupBtn(rowData) : stopSmGroupBtn(rowData)}
        </div>
        {/* ./ Play & Stop buttons */}

        {/* Action Buttons */}
        <span className={clsx('ml-2', 'flex-table-actions')}>{msGroupDeleteBtn(rowData)}</span>
        {/* ./ Action Buttons */}
      </>
    );
  };

  nameChanged = _.debounce((rowData: MockServiceGroup, name: string) => {
    if (rowData.name !== name) {
      const smGroup: MockServiceGroup = { ...rowData, name: name };
      this.props.updateMockServiceGroupPatch(smGroup, ['name']);
    }
  }, 600);

  getNameCellComponent = (group: MockServiceGroup) => {
    const isExpanded = this.isSmGroupExpanded(group);
    const groupNameInput = useFormInput(
      group.name,
      (fn: CallbackFn) => {
        if (fn.isValid) {
          //Do update
          this.nameChanged(group, fn.value as string);
        }
      },
      {
        type: ValidatorType.REQUIRED,
        invalidMessage: this.props.t('groups:errorMsgs.groupNameRequired'),
      },
      {
        type: ValidatorType.INVALID_CHARACTERS,
        invalidMessage: this.props.t('groups:errorMsgs.groupIllegalChars'),
      },
      {
        type: ValidatorType.MAX_LENGTH,
        value: 250,
        invalidMessage: this.props.t('groups:errorMsgs.groupMaximumLength'),
      },
      {
        type: ValidatorType.VALUE_FOUND_ON_ARRAY,
        value: this._getFilteredResults(group.name).map(s => s.name),
        invalidMessage: i18n.t('servicePicker.serviceNameAlreadyExists'),
      },
    );

    if (isExpanded && this.useLastValidValue) {
      groupNameInput.resetValue(group.name);
      this.useLastValidValue = false;
    }

    const expandLink = (
      <ExpandLink
        value={group.name}
        id={group.id}
        onToggleExpandDetail={() => {
          this.toggleExpandDetail(group.id);
        }}
      />
    );

    return (
      <>
        <div className={styles.invalidName + 'py-1'} style={{ width: '100%' }}>
          {isExpanded ? (
            <BzFormControlWithHook
              useFormInputHook={groupNameInput}
              disabled={group.status === 'CONFIGURING'}
              defaultValue={group.name}
              required={true}
              id="templateNewNameInput"
              className="form-control"
            />
          ) : (
            expandLink
          )}
        </div>
      </>
    );
  };

  /**
   * Checks whether virtual service Group row should be enabled for update.
   * @param smGroup
   */
  isSmGroupRowEnabled = (smGroup: MockServiceGroup) => {
    // Legacy endpoint fields: consider present when non-null/undefined and non-empty string
    const hasHttp = !_.isNil(smGroup.httpEndpoint) && String(smGroup.httpEndpoint).trim() !== '';
    const hasHttps = !_.isNil(smGroup.httpsEndpoint) && String(smGroup.httpsEndpoint).trim() !== '';

    // New endpoints array: consider present if the first element has a non-empty 'endpoint' value (array currently designed to contain at most one element)
    let hasEndpointsArray = false;
    if (Array.isArray(smGroup.endpoints) && smGroup.endpoints.length > 0) {
      const first = smGroup.endpoints[0] as any;
      hasEndpointsArray = Boolean(first && !_.isNil(first.endpoint) && String(first.endpoint).trim() !== '');
    }

    // A group is considered "new" (editable) when it has no legacy endpoints AND no endpoints in the array,
    // and it's not in a FAILED state. Also ensure it's not currently CONFIGURING.
    const isSmGroupNew = !hasHttp && !hasHttps && !hasEndpointsArray && smGroup.status !== 'FAILED';

    return isSmGroupNew && smGroup.status !== 'CONFIGURING';
    //TODO add more logic if needed.
  };

  smGroupLocationChanged = (rowData: MockServiceGroup, location: any) => {
    const changeLocation = () => {
      if (isOplLocation(location.harborId, location.shipId)) {
        const smGroup = { ...rowData, harborId: location.harborId, shipId: location.shipId };
        smGroup.serviceMocks = [];
        this.props.updateMockServiceGroupPatch(smGroup, ['harborId', 'shipId', 'serviceMocks']);
      }
    };

    //If there are no virtual service assigned yet, just change the location
    if (_.size(rowData.serviceMocks) === 0) {
      changeLocation();
    }
    //If there are some mocks assigned, warn user - they need to be de-assigned.
    else {
      const modalBody =
        'Are you sure you want to change the location of this group? All assigned mocks will be de-assigned from this group.';
      //Warn user that change of location will also de-assign all existing virtual services from the group
      confirm({
        confirmation: modalBody,
        yesOptionTitle: 'Proceed',
        title: 'Changing the location',
        yesOptionVariant: 'primary',
      })
        .then(() => {
          changeLocation();
        })
        .catch(_.noop);
    }
  };

  _displayTextualSmGroupLocationString = (rowData: MockServiceGroup) => {
    const locationString = getLocValue(
      rowData.harborId,
      rowData.shipId,
      this.props.locData,
      isPrivateDataEnabled(this.props.blazeUserConfig),
    );

    return (
      <span title={locationString} id={`smGroupLocationValue` + rowData.id}>
        {locationString}
      </span>
    );
  };

  SmGroupLocationCell = (rowData: MockServiceGroup) => {
    const isRowEnabled = this.isSmGroupRowEnabled(rowData);
    if (isRowEnabled) {
      return (
        <div id={`mockServiceLocationSelector` + rowData.id}>
          <Locations
            onChangeLocation={location => this.smGroupLocationChanged(rowData, location)}
            manualPreLoad={true}
            rowData={rowData}
            locationPickerState={this.props.isPrivateCloudReadOnly}
            filterBy={PROVIDER_OPL}
            locSettings={this.locSettings}
          />
        </div>
      );
    } else {
      return this._displayTextualSmGroupLocationString(rowData);
    }
  };
  toggleExpandDetail = (smGroupId: number) => {
    if (this.getTable()) {
      this.getTable().expandRowHandler(smGroupId);
    }
  };

  /**
   * Adds new blank Group to the list and opens it in the table.
   */
  addNewGroupHandler = async () => {
    // Show in progress toast for a while
    const toast: IToast = this.props.showToastNotification(
      ToastType.Info,
      this.props.t('groups:groups.createGroup'),
      this.props.t('groups:groups.creatingGroupMsg'),
    );

    const msGroup: MockServiceGroup = (await this.props.createBlankMockServiceGroup()) as any;

    // Once finished, expand the created smGroup row by id and paginate there
    this.getTable().expandRowHandler(msGroup.id);
    this.getTable().goToPageWithSelectedItem(msGroup.id);
  };

  /**
   * Expand table item action.
   */
  expandSmGroupItem = (smGroupId: number | null) => {
    this.props.expandGroup(smGroupId);
  };

  isSmGroupExpanded = (smGroup: MockServiceGroup) => {
    return this.props.expandedGroupId === smGroup.id;
  };

  ExpandDetailComponent = (smGroup: MockServiceGroup) => (
    <GroupDetail group={smGroup} disabled={smGroup.status === 'CONFIGURING'} />
  );

  endpointPreferenceChanged = (rowData: MockServiceGroup, newPreference: string) => {
    const smGroup = { ...rowData, endpointPreference: newPreference };
    this.props.updateMockServiceGroupPatch(smGroup, ['endpointPreference']);
  };

  preferredPortChanged = (rowData: MockServiceGroup, preferredPort: string) => {
    const smGroup = { ...rowData, preferredPort: preferredPort };
    this.props.updateMockServiceGroupPatch(smGroup, ['preferredPort']);
  };

  //converts from mockServiceGroup to mockService so to use same logic according to its sm properties
  setMockServiceProperties = (rowData: MockServiceGroup): MockService => {
    const mockService = ({ ...rowData } as unknown) as MockService;
    const isGroupNew = _.isNil(rowData.httpsEndpoint) && _.isNil(rowData.httpEndpoint);
    mockService.type = TRANSACTIONAL;
    mockService.metaData = {
      isDeployed: rowData.status === 'RUNNING',
      inProgress: rowData.status === 'CONFIGURING',
      isFailed: rowData.status === 'FAILED',
      isStopped: rowData.status === 'STOPPED' && !isGroupNew,
    } as MockServiceMetaData;
    const preferredPort = !_.isNil(rowData.preferredPort) ? String(rowData.preferredPort) : '';
    mockService.isNew = isGroupNew;
    mockService.metadata = { preferredPort: preferredPort } as MetaData;
    return mockService;
  };

  SmGroupEndpointCell = (rowData: MockServiceGroup) => {
    const mockService = this.setMockServiceProperties(rowData);
    return (
      <Endpoint
        minWidth={350}
        mockService={mockService}
        isRowEnabled={this.isSmGroupRowEnabled(rowData)}
        isPrivateCloudReadOnly={this.props.isPrivateCloudReadOnly}
        endpointPreferenceChanged={this.endpointPreferenceChanged}
        toggleExpandDetail={this.toggleExpandDetail}
        preferredPortChanged={this.preferredPortChanged}
        displayPortField={true}
        type={'group'}
      />
    );
  };

  SmGroupStatusCell = (rowData: MockServiceGroup) => {
    const mockService = this.setMockServiceProperties(rowData);
    return getStatusCell(mockService);
  };

  // Definition of table columns
  colsDefinition: BlazeTableColDef[] = [
    {
      header: this.props.t('groups:mockServiceGroupTable.columnNames.mockGroupName'),
      cellComponent: (rowData: any) => this.getNameCellComponent(rowData),
      accessor: 'name',
      cellClass: 'flex-table-col-overflow-visible',
    },
    {
      header: this.props.t('groups:mockServiceGroupTable.columnNames.location'),
      width: 140,
      cellComponent: (rowData: MockServiceGroup) => this.SmGroupLocationCell(rowData),
      cellClass: (rowData: MockServiceGroup) => (this.isSmGroupRowEnabled(rowData) ? 'has-flex-table-dropdown' : ''),
      accessor: 'harborId',
    },
    {
      header: this.props.t('groups:mockServiceGroupTable.columnNames.endpoint'),
      headerComponent: (
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'relative', bottom: '3px' }}>
            {this.props.t('groups:mockServiceGroupTable.columnNames.endpoint')}
          </span>
          <span id="serviceHelp" className={styles.questionInTableHeader} title={this.props.t('common:showHelp')}>
            <HelpTooltip id={'creating-a-mock-service-help'} />
          </span>
        </div>
      ),
      cellComponent: (rowData: MockServiceGroup) => this.SmGroupEndpointCell(rowData),
      cellClass: (rowData: MockServiceGroup) =>
        this.isSmGroupRowEnabled(rowData) ? 'flex-table-col-overflow-visible' : 'has-flex-table-dropdown',
      accessor: 'endpoint',
    },
    {
      header: this.props.t('groups:mockServiceGroupTable.columnNames.tags'),
      accessor: 'tags',
      width: 190,
      cellComponent: (rowData: MockServiceGroup) => {
        return (
          <div className="picked">
            {rowData != null &&
              rowData.tags != null &&
              _.sortBy(rowData.tags).map((tag: string, index: number) => {
                return (
                  <span key={index} className="picked-item mr-2" title={tag}>
                    {tag}{' '}
                  </span>
                );
              })}
          </div>
        );
      },
    },
    {
      header: this.props.t('groups:mockServiceGroupTable.columnNames.status'),
      width: 130,
      cellComponent: (rowData: any) => this.SmGroupStatusCell(rowData),
      accessor: 'status',
    },
    {
      header: this.props.t('groups:mockServiceGroupTable.columnNames.actions'),
      cellComponent: (rowData: any) => this.getRightSideButtonsCell(rowData),
      accessor: 'serviceId',
      isSortable: false,
      cellClass: 'flex-table-col-overflow-visible',
      width: 160,
    },
  ];

  tableOptions: BlazeTableOptions = {
    expandDetailComponent: (rowData: any) => this.ExpandDetailComponent(rowData),
    showRowExpander: true,
    columnDef: this.colsDefinition,
    pageable: true,
    selectable: true,
    sortable: true,
    pageSize: 10,
    rowPadding: 8,
    customRowClassName: 'blazeTableRow',
    tableName: 'mockServiceGroups',
    noResultsText: this.props.t('groups:mockServiceGroupTable.noMockServiceAvailable'),
    minWidth: 750,
    disabled: this.props.isPrivateCloudReadOnly,
  };

  createAddNewGroupDom = () => {
    const { t } = this.props;
    return (
      <div className={'float-right'}>
        <button
          disabled={this.props.isPrivateCloudReadOnly}
          title={t('groups:mockServiceGroupTable.createNew')}
          onClick={() => this.addNewGroupHandler()}
          id="createServiceMockButton"
          className={`btn btn-primary btn-circular btn-jb ${styles.addMockServiceIcon}`}
        >
          <BzmIcon variant={'icon-bzm-add'} />
        </button>
      </div>
    );
  };

  _getFilteredResults = (name?: string) => {
    let filteredGroups = _.cloneDeep(this.props.mockServiceGroupList);
    if (!_.isEmpty(this.props.currentSearchTerm)) {
      filteredGroups = applySearchTermFilterToListWithTags(filteredGroups, this.props.currentSearchTerm);
    }
    if (name !== undefined) {
      filteredGroups = filteredGroups.filter(smG => smG.name !== name);
    }
    return filteredGroups;
  };

  disableBulkDelete = () => {
    return this.state.selectedSmGroups.length === 0;
  };

  /**
   * Disables the bulk START button when no groups are selected, or when all
   * selected groups are already RUNNING or CONFIGURING (nothing to start).
   * Mirrors the Virtual Service Tab behaviour.
   */
  disableBulkStart = () => {
    const { selectedSmGroups } = this.state;
    if (selectedSmGroups.length === 0) {
      return true;
    }
    return selectedSmGroups.every(
      (group: MockServiceGroup) => group.status === 'RUNNING' || group.status === 'CONFIGURING',
    );
  };

  /**
   * Disables the bulk STOP button when no groups are selected, or when none of
   * the selected groups are actually RUNNING (only RUNNING groups can be stopped).
   * Mirrors the Virtual Service Tab behaviour.
   */
  disableBulkStop = () => {
    const { selectedSmGroups } = this.state;
    if (selectedSmGroups.length === 0) {
      return true;
    }
    return !selectedSmGroups.some((group: MockServiceGroup) => group.status === 'RUNNING');
  };

  /**
   * Clears the table selection visually and resets the local selected-groups
   * state.  Call this after any bulk action so the bulk buttons become greyed
   * out until the user makes a new selection.
   */
  clearSelection = () => {
    if (this.tableRef) {
      this.tableRef.deSelectAll();
    }
  };

  /** Dispatches a bulk START for all selected groups, then clears selection. */
  onBulkStartClicked = () => {
    this.props.bulkStartAction(this.state.selectedSmGroups);
    this.clearSelection();
  };

  /** Dispatches a bulk STOP for all selected groups, then clears selection. */
  onBulkStopClicked = () => {
    this.props.bulkStopAction(this.state.selectedSmGroups);
    this.clearSelection();
  };

  onBulkDeleteClicked = () => {
    const modalBody = (
      <p>
        {i18n.t('marFiles:table.bulkDeleteWarn')}{' '}
        <strong className="text-break">{this.state.selectedSmGroups.length}</strong>{' '}
        {this.state.selectedSmGroups.length === 1
          ? ` ${i18n.t('groups:mockServiceGroupTable.bulkDeleteSingleGroup')}`
          : ` ${i18n.t('groups:mockServiceGroupTable.bulkDeleteMultipleGroups')}`}
        <br />
        {i18n.t('mockServices:mockServicesTable.cannotUndo')}
      </p>
    );
    confirm({
      confirmation: modalBody,
      yesOptionTitle: `${i18n.t('common:actions.delete')}`,
      title: `${i18n.t('groups:mockServiceGroupTable.deleteSelectedMsGroup')}`,
    })
      .then(() => {
        this.props.bulkDeleteAction(this.state.selectedSmGroups);
        this.clearSelection();
      })
      .catch(() => {
        //User pressed No
      });
  };

  render() {
    const { t } = this.props;
    const { selectedSmGroups } = this.state;
    const filteredGroups = this._getFilteredResults();
    const headerText = getSelectedCountTitle(filteredGroups.length, selectedSmGroups.length);

    return (
      <React.Fragment>
        <div id="serviceMockGroupsTitle" className="mb-4">
          {/* Add New Group*/}
          {this.createAddNewGroupDom()}
          {/* ./ Add New Group*/}
          {/* ./ Refresh button*/}
          <Button
            className={'float-right mr-4'}
            variant="secondary"
            style={{ marginTop: '11px' }}
            disabled={false}
            onClick={() => {
              this.props.getMockServiceGroups();
            }}
          >
            <BzmIcon variant="icon-bzm-sync" />
            <span className="ml-2">{t('mockServicesTable.refresh')}</span>
          </Button>
          {/* ./ Refresh button*/}
          <h2 className={styles.title + ' mb-2'}>
            {t('groups:mockServiceGroupTable.title')} {headerText}
            <ElevioTooltip elevioId={'mock-service-groups-help'} />
            <button
              id="qa-blk-grp-start"
              className="btn btn-link btn-no-hover p-0 mx-2"
              title={t('mockServicesTable.runSelectedMs')}
              onClick={this.onBulkStartClicked}
              disabled={this.disableBulkStart() || this.props.isPrivateCloudReadOnly}
            >
              <BzmIcon variant={'icon-bzm-play-circle'} className={'icon-sm'} />
            </button>
            <button
              id="qa-blk-grp-stop"
              className="btn btn-link btn-no-hover p-0 mx-2"
              title={t('mockServicesTable.stopSelectedMs')}
              onClick={this.onBulkStopClicked}
              disabled={this.disableBulkStop() || this.props.isPrivateCloudReadOnly}
            >
              <BzmIcon variant={'icon-bzm-stop-circle'} className={'icon-sm'} />
            </button>
            <button
              id="qa-blk-grp-delete"
              className="btn btn-link btn-no-hover p-0 mx-2"
              title={t('groups:mockServiceGroupTable.deleteSelectedMsGroup')}
              onClick={this.onBulkDeleteClicked}
              disabled={this.disableBulkDelete() || this.props.isPrivateCloudReadOnly}
            >
              <BzmIcon variant={'icon-bzm-delete'} className={'icon-sm'} />
            </button>
          </h2>
          <p id="serviceMockVerbiage">{t('groups:mockServiceGroupTable.description')}</p>
        </div>

        <BlazeTable
          data={filteredGroups}
          options={this.tableOptions}
          isLoading={this.props.isLoading}
          expandedItem={this.props.expandedGroupId}
          onExpandedItemChange={this.expandSmGroupItem}
          onRef={(ref: any) => (this.tableRef = ref)}
          onSelectionChange={this.onSelectionChange}
        />
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state: ApplicationState) => ({
  mockServiceGroupList: state.mockServiceGroup.mockServiceGroups,
  isLoading: state.mockServiceGroup.isLoading,
  expandedGroupId: state.mockServiceGroup.expandedGroupId,
  locData: state.mockservices.locData,
  services: state.services.services,
  currentSearchTerm: state.mockServiceGroup.filter.search,
  activePage: state.mockServiceGroup.activePage,
  isSaving: state.mockServiceGroup.isSaving,
  isPrivateCloudReadOnly: state.blazeUserConfig.isPrivateCloudReadOnly,
  blazeUserConfig: state.blazeUserConfig.blazeUserConfig,
});

const mapDispatchToProps = {
  getMockServiceGroups: getMockServiceGroupList,
  deleteMockServiceGroup,
  createBlankMockServiceGroup,
  showToastNotification,
  hideToast,
  fetchServices,
  getConfigurationsListAction,
  expandGroup,
  updateMockServiceGroupPatch,
  bulkDeleteAction,
  bulkStartAction,
  bulkStopAction,
  deployMockServiceGroup,
  stopMockServiceGroup,
};

type DispatchProps = typeof mapDispatchToProps;
type StateProps = ReturnType<typeof mapStateToProps>;

export default withTranslation('mockServices')(connect(mapStateToProps, mapDispatchToProps)(MockGroupListBlazeTable));
